/**
 * ============================================================================
 * SKINNER & VALE — CART EDIT DESIGN INTEGRATION
 * ============================================================================
 * Adds "Edit Design" buttons to customised jacket items in the Shopify cart.
 * Works with saved designs in localStorage to enable editing from cart.
 *
 * Include in cart template or theme.liquid:
 *   <script src="{{ 'sv-cart-edit-design.js' | asset_url }}" defer></script>
 *
 * FIXES (2026-02-28):
 *   - Correct URL: /pages/customisation (matches live Shopify page template)
 *   - hasDesign() null-check bug fixed (undefined vs null)
 *   - fetch patch now re-throws on network error (no silent swallowing)
 *   - querySelectorAll('*') scoped to cart container (performance fix)
 *   - Polling only starts on cart pages (stops memory leak on non-cart pages)
 *   - console.log gated behind DEBUG flag (silent in production)
 *   - Debounce reduced from 150ms → 80ms for faster AJAX themes
 *   - Button styles moved to CSS class .sv-edit-design-btn (no inline style CSP issues)
 *   - SVG pencil icon has aria-hidden="true"
 *   - Button wrapper has box-sizing & max-width for mobile safety
 *   - MutationObserver attributeFilter expanded to catch more AJAX updates
 * ============================================================================
 */

(function () {
  'use strict';

  var SVCartEditDesign = {

    // ── Configuration ────────────────────────────────────────────────────────
    // Live Shopify page template (templates/page.customisation)
    CUSTOMIZER_PATH: '/pages/customisation',

    // localStorage key used by jacket.js to store saved designs
    STORAGE_KEY: 'jacket-cart-designs',

    // Polling interval in ms — only runs on cart pages
    POLL_INTERVAL: 1500,

    // Set to true in browser console to see debug logs: SVCartEditDesign.DEBUG = true
    DEBUG: false,

    // ── Internal state ────────────────────────────────────────────────────────
    observer: null,
    pollTimer: null,
    pollMissCount: 0,    // consecutive "nothing found" counts before stopping poll
    updateTimeout: null,
    cartContainer: null,

    // ── Logging helper ────────────────────────────────────────────────────────
    log: function () {
      if (this.DEBUG) {
        var args = Array.prototype.slice.call(arguments);
        args.unshift('🛒 S&V:');
        console.log.apply(console, args);
      }
    },

    // ── Initialise ────────────────────────────────────────────────────────────
    init: function () {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.setup.bind(this));
      } else {
        this.setup();
      }
    },

    setup: function () {
      // Resolve cart container once — shared by observer and scoped queries
      this.cartContainer = this.findCartContainer();

      this.addEditButtonsToCartItems();
      this.watchForCartUpdates();
      this.listenForCartEvents();

      // Only poll if we are actually on a cart-related page
      if (this.isCartPage()) {
        this.startPolling();
      }

      this.log('ready');
    },

    // ── Cart page detection ───────────────────────────────────────────────────
    isCartPage: function () {
      return (
        window.location.pathname.includes('/cart') ||
        !!document.querySelector('form[action="/cart"]') ||
        !!document.querySelector('[data-cart]')
      );
    },

    // ── Find cart container (once, cached) ───────────────────────────────────
    findCartContainer: function () {
      var selectors = [
        'form[action="/cart"]',
        '[data-cart]',
        '.cart__items',
        '.cart-drawer',
        '#CartDrawer',
        '.ajaxcart',
        '.cart',
        '#cart',
        'main'
      ];
      for (var i = 0; i < selectors.length; i++) {
        try {
          var el = document.querySelector(selectors[i]);
          if (el) {
            this.log('cart container found via', selectors[i]);
            return el;
          }
        } catch (e) {
          // Invalid selector — skip
        }
      }
      return document.body;
    },

    // ── Add Edit buttons to all eligible cart items ───────────────────────────
    addEditButtonsToCartItems: function () {
      var scope = this.cartContainer || document;

      // Try standard cart item selectors (scoped to cart container)
      var cartItemSelectors = [
        '[data-cart-item]',
        '.cart-item',
        '.cart__item',
        'tr.cart-item',
        '.cart__row',
        '.cart-drawer__item',
        '.ajaxcart__product',
        '[data-cart-item-key]',
        '.line-item',
        '.cart__item-row',
        '.cart__items .cart__row',
        '[class*="cart"][class*="item"]',
        '[class*="line-item"]'
      ];

      var cartItems = [];

      for (var si = 0; si < cartItemSelectors.length; si++) {
        try {
          var found = scope.querySelectorAll(cartItemSelectors[si]);
          if (found.length > 0) {
            cartItems = Array.prototype.slice.call(found);
            this.log('found', found.length, 'cart items via', cartItemSelectors[si]);
            break;
          }
        } catch (e) {
          // Bad selector — skip
        }
      }

      // Fallback: search within scope only for "Design ID:" text
      if (cartItems.length === 0) {
        var scopeElements = scope.querySelectorAll('dt, dd, span, p, td, li, div');
        scopeElements.forEach(function (el) {
          if (
            el.textContent &&
            el.textContent.includes('Design ID:') &&
            !el.querySelector('.sv-edit-design-btn') &&
            el.tagName !== 'SCRIPT' &&
            el.tagName !== 'STYLE'
          ) {
            var parent = el;
            for (var depth = 0; depth < 10; depth++) {
              parent = parent.parentElement;
              if (!parent) break;
              var cls = parent.className || '';
              var tag = parent.tagName || '';
              if (
                cls.includes('item') || cls.includes('row') ||
                tag === 'TR' || cls.includes('product')
              ) {
                if (cartItems.indexOf(parent) === -1) cartItems.push(parent);
                break;
              }
            }
          }
        });
      }

      var addedCount = 0;
      var self = this;

      cartItems.forEach(function (item) {
        var designId = self.extractDesignId(item);
        if (designId && !item.querySelector('.sv-edit-design-btn')) {
          self.addEditButton(item, designId);
          addedCount++;
        }
      });

      if (addedCount > 0) {
        this.log('added', addedCount, 'edit button(s)');
      }

      return addedCount;
    },

    // ── Extract Design ID from a cart item element ────────────────────────────
    extractDesignId: function (item) {
      // Try Shopify line item property element first
      var propEl = item.querySelector('[data-property-name="Design ID"]');
      if (propEl) {
        return propEl.textContent.replace(/Design ID[:\s]*/i, '').trim() || null;
      }

      // Regex patterns — most specific (SV- format) first
      var patterns = [
        /SV-\d{8}-\d{6}-[A-Z0-9]+/,
        /Design ID[:\s]+([A-Z0-9-]+)/i
      ];

      var text = item.textContent || '';
      for (var pi = 0; pi < patterns.length; pi++) {
        var match = text.match(patterns[pi]);
        if (match) {
          return (match[1] || match[0]).trim();
        }
      }
      return null;
    },

    // ── Inject the Edit Design button into a cart item ────────────────────────
    addEditButton: function (cartItem, designId) {
      var editUrl = this.CUSTOMIZER_PATH + '?edit=' + encodeURIComponent(designId);

      var wrapper = document.createElement('div');
      wrapper.className = 'sv-edit-design-wrapper';

      var link = document.createElement('a');
      link.href = editUrl;
      link.className = 'sv-edit-design-btn';
      link.setAttribute('data-design-id', designId);
      link.setAttribute('role', 'button');
      link.setAttribute('title', 'Return to the jacket customiser to edit this design');
      link.innerHTML =
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" ' +
        'stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
        'stroke-linejoin="round" aria-hidden="true">' +
        '<path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>' +
        '</svg>' +
        '<span>Edit Design</span>';

      wrapper.appendChild(link);

      // Preferred insertion points — after the last property row
      var insertLocations = [
        cartItem.querySelector('.cart-item__property'),
        cartItem.querySelector('.product-option'),
        cartItem.querySelector('[data-cart-item-property]'),
        cartItem.querySelector('.cart__item-details'),
        cartItem.querySelector('.cart__item-sub'),
        cartItem.querySelector('.line-item__details'),
        cartItem.querySelector('[class*="property"]'),
        cartItem.querySelector('[class*="details"]'),
        cartItem.querySelector('dd:last-of-type'),
        cartItem.querySelector('td:last-of-type')
      ];

      var inserted = false;
      for (var li = 0; li < insertLocations.length; li++) {
        var loc = insertLocations[li];
        if (loc && loc.parentNode) {
          try {
            loc.parentNode.insertBefore(wrapper, loc.nextSibling);
            inserted = true;
            break;
          } catch (e) {
            // Try next
          }
        }
      }
      if (!inserted) {
        cartItem.appendChild(wrapper);
      }

      this.log('edit button added for design', designId);
    },

    // ── MutationObserver — watch for AJAX cart DOM changes ───────────────────
    watchForCartUpdates: function () {
      if (!this.cartContainer) return;

      if (this.observer) this.observer.disconnect();

      var self = this;

      this.observer = new MutationObserver(function (mutations) {
        var shouldUpdate = false;
        for (var mi = 0; mi < mutations.length; mi++) {
          var m = mutations[mi];
          if (m.addedNodes.length > 0 || m.removedNodes.length > 0) {
            shouldUpdate = true;
            break;
          }
          if (
            m.type === 'attributes' &&
            m.target.className &&
            (m.target.className.includes('cart') || m.target.className.includes('item'))
          ) {
            shouldUpdate = true;
            break;
          }
        }

        if (shouldUpdate) {
          clearTimeout(self.updateTimeout);
          self.updateTimeout = setTimeout(function () {
            self.log('cart DOM changed — re-injecting edit buttons');
            self.addEditButtonsToCartItems();
          }, 80); // 80ms — fast enough for Turbo/instant-page themes
        }
      });

      this.observer.observe(this.cartContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'data-cart-item', 'data-item-key', 'hidden', 'style']
      });

      this.log('MutationObserver watching cart container');
    },

    // ── Polling fallback — only on cart pages ─────────────────────────────────
    startPolling: function () {
      if (this.pollTimer) clearInterval(this.pollTimer);
      var self = this;

      this.pollTimer = setInterval(function () {
        var designIdMatches =
          document.body.textContent.match(/Design ID[:\s]+([A-Z0-9-]+)/gi) || [];
        var existingBtns = document.querySelectorAll('.sv-edit-design-btn');

        if (designIdMatches.length > existingBtns.length) {
          self.log('polling: missing buttons detected — re-injecting');
          self.pollMissCount = 0;
          self.addEditButtonsToCartItems();
        } else {
          // Stop polling after 20 consecutive clean passes (~30 seconds)
          // — the observer will handle any future changes
          self.pollMissCount++;
          if (self.pollMissCount > 20) {
            clearInterval(self.pollTimer);
            self.pollTimer = null;
            self.log('polling stopped — observer is handling updates');
          }
        }
      }, this.POLL_INTERVAL);
    },

    // ── Shopify / AJAX cart event listeners ───────────────────────────────────
    listenForCartEvents: function () {
      var self = this;
      var delay = 250;

      var reInject = function (source) {
        self.log('event from', source, '— re-injecting');
        setTimeout(function () { self.addEditButtonsToCartItems(); }, delay);
      };

      document.addEventListener('cart:refresh',        function () { reInject('cart:refresh'); });
      document.addEventListener('cart:updated',        function () { reInject('cart:updated'); });
      document.addEventListener('ajaxCart.afterCartLoad', function () { reInject('ajaxCart.afterCartLoad'); });

      // Intercept fetch — detect Shopify cart API calls
      var originalFetch = window.fetch;
      window.fetch = function () {
        var fetchArgs = arguments;
        return originalFetch.apply(window, fetchArgs).then(function (response) {
          var url = typeof fetchArgs[0] === 'string'
            ? fetchArgs[0]
            : (fetchArgs[0] && fetchArgs[0].url ? fetchArgs[0].url : '');
          if (
            url.includes('/cart') &&
            (url.includes('.js') || url.includes('change') || url.includes('update') || url.includes('add'))
          ) {
            reInject('fetch:' + url.split('?')[0].split('/').pop());
          }
          return response;
        }).catch(function (err) {
          // Always re-throw so calling code receives the error
          throw err;
        });
      };

      this.log('cart event listeners registered');
    },

    // ── LocalStorage helpers ──────────────────────────────────────────────────
    getSavedDesign: function (designId) {
      try {
        var designs = JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '[]');
        return designs.find(function (d) { return d.designId === designId; }) || null;
      } catch (e) {
        return null;
      }
    },

    // Fixed: .find() returns undefined when not found — coerce to boolean correctly
    hasDesign: function (designId) {
      return this.getSavedDesign(designId) !== null;
    },

    // ── Manual refresh (callable from console) ────────────────────────────────
    refresh: function () {
      this.log('manual refresh');
      this.addEditButtonsToCartItems();
    }
  };

  // ── Button styles injected as a <style> tag (avoids CSP inline-style issues)
  (function injectStyles() {
    if (document.getElementById('sv-cart-edit-styles')) return;
    var style = document.createElement('style');
    style.id = 'sv-cart-edit-styles';
    style.textContent = [
      '.sv-edit-design-wrapper {',
      '  margin: 12px 0;',
      '  clear: both;',
      '  box-sizing: border-box;',
      '  max-width: 100%;',
      '}',
      '.sv-edit-design-btn {',
      '  display: inline-flex;',
      '  align-items: center;',
      '  gap: 8px;',
      '  background: #1a1a1a;',
      '  color: #ffffff;',
      '  text-decoration: none;',
      '  padding: 10px 18px;',
      '  border-radius: 6px;',
      '  font-size: 13px;',
      '  font-weight: 600;',
      '  letter-spacing: 0.3px;',
      '  -webkit-transition: background 0.2s ease, -webkit-transform 0.2s ease;',
      '  transition: background 0.2s ease, transform 0.2s ease;',
      '  border: none;',
      '  cursor: pointer;',
      '  box-sizing: border-box;',
      '  max-width: 100%;',
      '}',
      '.sv-edit-design-btn:hover {',
      '  background: #333333;',
      '  -webkit-transform: translateY(-1px);',
      '  transform: translateY(-1px);',
      '}',
      '.sv-edit-design-btn:focus-visible {',
      '  outline: 3px solid #3b82f6;',
      '  outline-offset: 2px;',
      '}',
      /* Mobile: full-width at small sizes */
      '@media (max-width: 480px) {',
      '  .sv-edit-design-btn {',
      '    width: 100%;',
      '    justify-content: center;',
      '    padding: 12px 18px;',
      '  }',
      '}'
    ].join('\n');
    document.head.appendChild(style);
  })();

  // ── Bootstrap ─────────────────────────────────────────────────────────────
  SVCartEditDesign.init();

  // Expose globally for console debugging / external scripts
  window.SVCartEditDesign = SVCartEditDesign;

})();
